import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {RootState} from "@/store/RootState";
import responseHandler from "@/plugins/errorHandling";

export interface ITranslationEntry {
  group: string;
  item: string;
  translations: { [locale: string]: string };
}

export interface ITranslationMeta {
  groups: string[];
  languages: { code: string; name: string }[];
}

interface TranslationState {
  translations: ITranslationEntry[];
  meta: ITranslationMeta;
  errors: IErrors;
}

const state: TranslationState = {
  translations: [],
  meta: { groups: [], languages: [] },
  errors: {},
};

const getters: GetterTree<TranslationState, RootState> = {
  translations: (state): ITranslationEntry[] => state.translations,
  meta: (state): ITranslationMeta => state.meta,
  errors: (state) => state.errors,
};

const actions: ActionTree<TranslationState, RootState> = {
  fetchTranslations: ({ commit }, params: { search?: string; filter?: string; namespace?: string } = {}): any => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.namespace) queryParams.append('namespace', params.namespace);
    const qs = queryParams.toString();

    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'settings/translations' + (qs ? '?' + qs : ''),
      }),
      commit,
      'translationsLoaded'
    );
  },
  fetchMeta: ({ commit }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'settings/translations/meta',
    }),
    commit,
    'metaLoaded'
  ),
  saveTranslation: ({ commit }, entry: { group: string; item: string; translations: { [locale: string]: string }; namespace?: string }): any =>
    responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'settings/translations',
        method: 'POST',
        data: entry,
      }),
      commit,
      'translationSaved'
    ),
  createTranslation: ({ commit }, entry: { group: string; item: string; translations: { [locale: string]: string }; namespace?: string }): any =>
    responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'settings/translations/create',
        method: 'POST',
        data: entry,
      }),
      commit,
      'translationsLoaded'
    ),
  copyToChannel: ({ commit }, data: { translation_key: string; namespace: string }): any =>
    responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'settings/translations/copy-to-channel',
        method: 'POST',
        data,
      }),
      commit,
      'translationsLoaded'
    ),
};

const mutations: MutationTree<TranslationState> = {
  translationsLoaded(state, payload) {
    state.translations = payload;
  },
  metaLoaded(state, payload) {
    state.meta = payload;
  },
  translationSaved(state, payload) {
    // Update the single entry in the list
    const key = payload.group + '.' + payload.item;
    const idx = state.translations.findIndex(
      (t) => t.group + '.' + t.item === key
    );
    if (idx >= 0) {
      state.translations.splice(idx, 1, payload);
    }
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
};

const namespaced: boolean = true;

export const translationModule: Module<TranslationState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
